import { MouseEvent, ReactNode } from "react";
import { useHref, useNavigate } from "react-router-dom";
import styles from "./Table.module.css";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  width?: string;
  /** "right" for a numeric column — the header follows its figures. */
  align?: "left" | "right";
  /** Extra class on this column's body cells. */
  cellClassName?: string;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  empty?: ReactNode;
  rowClassName?: (row: T) => string | undefined;
  /**
   * Makes a click anywhere on the row follow this address. Only the mouse is
   * served here: the row stays a table row for assistive technology, so the
   * caller puts a real link to the same address in one of its cells for the
   * keyboard.
   */
  rowHref?: (row: T) => string;
}

export function Table<T>({
  columns,
  rows,
  empty,
  rowClassName,
  rowHref,
}: Props<T>) {
  if (rows.length === 0 && empty) {
    return <div className={styles.empty}>{empty}</div>;
  }
  const alignCls = (c: Column<T>) =>
    c.align === "right" ? styles.right : undefined;
  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={alignCls(c)} style={{ width: c.width }}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const cls = [rowHref ? styles.clickable : "", rowClassName?.(row) ?? ""]
              .filter(Boolean)
              .join(" ");
            const cells = columns.map((c) => (
              <td
                key={c.key}
                className={
                  [alignCls(c), c.cellClassName].filter(Boolean).join(" ") ||
                  undefined
                }
              >
                {c.cell(row)}
              </td>
            ));
            return rowHref ? (
              <LinkedRow key={i} href={rowHref(row)} className={cls}>
                {cells}
              </LinkedRow>
            ) : (
              <tr key={i} className={cls || undefined}>
                {cells}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A row that follows its address on a click the way a link would: a modified
 * or middle click opens a new tab, a click on a control inside the row is that
 * control's, and a click that ends a text selection selects rather than leaves.
 */
function LinkedRow({
  href,
  className,
  children,
}: {
  href: string;
  className: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const tabHref = useHref(href);
  const follow = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.button > 1) return;
    if ((e.target as Element).closest("a, button, input, select, textarea")) return;
    if (window.getSelection()?.toString()) return;
    if (e.button === 1 || e.metaKey || e.ctrlKey || e.shiftKey) {
      window.open(tabHref, "_blank", "noopener");
      return;
    }
    navigate(href);
  };
  return (
    <tr className={className} onClick={follow} onAuxClick={follow}>
      {children}
    </tr>
  );
}
